export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
  requiredPermission?: string;
}

export interface ValidationError {
  field: string;
  messages: string[];
}

export function parseApiError(error: any): ApiError {
  const defaultMessage = "An unexpected error occurred. Please try again.";

  if (!error) {
    return { message: defaultMessage };
  }

  if (error.response) {
    const { data, status } = error.response;

    if (data?.message) {
      return {
        message: data.message,
        errors: data.errors || {},
        statusCode: status,
      };
    }

    if (data?.error) {
      return {
        message: typeof data.error === "string" ? data.error : defaultMessage,
        errors: data.errors || {},
        statusCode: status,
      };
    }

    switch (status) {
      case 400:
        return {
          message: "Invalid request. Please check your input.",
          statusCode: status,
        };
      case 401:
        return {
          message: "You are not authorized. Please log in again.",
          statusCode: status,
        };
      case 403:
        let permissionMsg = "You don't have permission to perform this action.";
        let requiredPermission;

        if (data?.message?.includes("Required permission:")) {
          const match = data.message.match(/Required permission: ([\w:*\-]+)/);
          if (match && match[1]) {
            requiredPermission = match[1];
            permissionMsg = `Access denied. Required permission: ${requiredPermission}`;
          }
        } else if (data?.message?.includes("super admin")) {
          permissionMsg = "Only super admins can perform this action.";
        } else if (data?.message) {
          permissionMsg = data.message;
        }

        return {
          message: permissionMsg,
          requiredPermission,
          statusCode: status,
        };
      case 404:
        return {
          message: "The requested resource was not found.",
          statusCode: status,
        };
      case 409:
        return {
          message: "A conflict occurred. The resource may already exist.",
          statusCode: status,
        };
      case 422:
        return {
          message: "Validation failed. Please check your input.",
          errors: data?.errors || {},
          statusCode: status,
        };
      case 429:
        return {
          message: "Too many requests. Please try again later.",
          statusCode: status,
        };
      case 500:
      case 502:
      case 503:
        return {
          message: "Server error. Please try again later.",
          statusCode: status,
        };
      default:
        return {
          message: defaultMessage,
          statusCode: status,
        };
    }
  }

  if (error.request) {
    return {
      message: "Network error. Please check your internet connection.",
    };
  }

  if (error.message) {
    return {
      message: error.message,
    };
  }

  return { message: defaultMessage };
}

export function formatValidationErrors(
  errors?: Record<string, string[]>
): ValidationError[] {
  if (!errors || Object.keys(errors).length === 0) {
    return [];
  }

  return Object.entries(errors).map(([field, messages]) => ({
    field,
    messages: Array.isArray(messages) ? messages : [String(messages)],
  }));
}

export function getErrorMessage(error: any, defaultMsg?: string): string {
  const apiError = parseApiError(error);
  return apiError.message || defaultMsg || "An error occurred";
}

export function getUserFriendlyMessage(operation: string, error: any): string {
  const apiError = parseApiError(error);

  const operationMessages: Record<string, string> = {
    create: "creating",
    update: "updating",
    delete: "deleting",
    fetch: "loading",
    save: "saving",
  };

  const action = operationMessages[operation] || operation;

  if (apiError.statusCode === 401) {
    return "Your session has expired. Please log in again.";
  }

  if (apiError.statusCode === 403) {
    return `You don't have permission for ${action} this item.`;
  }

  if (apiError.statusCode === 404) {
    return `The item you're trying to access was not found.`;
  }

  if (apiError.statusCode === 409) {
    return `This item already exists or conflicts with existing data.`;
  }

  if (apiError.message) {
    return apiError.message;
  }

  return `Failed ${action}. Please try again.`;
}

export function extractRequiredPermission(error: any): string | null {
  const apiError = parseApiError(error);

  if (apiError.requiredPermission) {
    return apiError.requiredPermission;
  }

  if (apiError.statusCode === 403 && error?.response?.data?.message) {
    const match = error.response.data.message.match(/Required permission: ([\w:*\-]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}
